import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const stagger = {
    parent: {
      animate: { transition: { staggerChildren: 0.15 } },
    },
    child: {
      initial: { opacity: 0, y: 40 },
      animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
      },
    },
  };

  return (
    <section ref={ref} className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Parallax Background Image */}
      <motion.div className="absolute inset-0" style={{ y }}>
        <img
          src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80"
          alt="Luxury property"
          className="w-full h-[120%] object-cover"
        />
      </motion.div>

      {/* Multi-layer Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 via-charcoal/50 to-charcoal/20" />
      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />

      {/* Decorative Elements */}
      {/* Corner Frame */}
      <div className="hidden lg:block absolute top-32 right-16 w-48 h-48 border-t border-r border-gold/20" />
      {/* Vertical Gold Line */}
      <div className="hidden lg:block absolute top-1/2 right-24 w-px h-32 -translate-y-1/2 bg-gradient-to-b from-transparent via-gold/30 to-transparent" />
      {/* Dot Pattern */}
      <div className="hidden lg:block absolute bottom-32 right-32 w-24 h-24 dot-pattern opacity-10" />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full"
      >
        <motion.div
          className="max-w-2xl"
          variants={stagger.parent}
          initial="initial"
          animate="animate"
        >
          <motion.div variants={stagger.child} className="flex items-center gap-4 mb-6">
            <span className="h-px w-8 bg-gold/40" />
            <span className="text-xs font-sans font-medium uppercase tracking-widest-plus text-gold-light">
              Welcome to Avdhoot Group
            </span>
          </motion.div>

          <motion.h1
            variants={stagger.child}
            className="text-4xl sm:text-5xl lg:text-7xl font-serif font-bold text-white leading-[1.1] mb-6"
          >
            Building Dreams
            <br />
            <span className="text-gradient-gold">Into Reality</span>
          </motion.h1>

          <motion.p
            variants={stagger.child}
            className="text-cream-200/80 font-sans text-lg sm:text-xl leading-relaxed mb-10 max-w-lg"
          >
            Burhanpur's most trusted real estate developer, creating premium living spaces with unmatched quality and timeless design.
          </motion.p>

          <motion.div
            variants={stagger.child}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/projects" className="btn-premium-filled">
              <span>Explore Projects</span>
            </Link>
            <Link
              to="/contact"
              className="btn-premium border-white/30 text-white hover:text-charcoal"
            >
              <span>Contact Us</span>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
          className="w-6 h-10 border border-white/30 rounded-full flex items-start justify-center pt-2"
        >
          <div className="w-1 h-2 bg-gold rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
